import type { IFixedJackpotEvent } from '~/interfaces/jackpots/fixed';
import type {
  IProgressiveJackpotResponse,
  IProgressiveJackpotState,
} from '~/interfaces/jackpots/progressive';

const mapJackpot = (
  jackpotResponse: IProgressiveJackpotResponse,
): IProgressiveJackpotState | null => {
  if (!jackpotResponse) return null;

  const isPaused = jackpotResponse.isActive && jackpotResponse.isDisabled;
  const status: IProgressiveJackpotState['status'] = jackpotResponse.isDisabled
    ? jackpotResponse.isActive
      ? 'paused'
      : 'disabled'
    : jackpotResponse.isActive
      ? 'active'
      : 'idle';

  const baseTimestamp = jackpotResponse.jackpotAmountTimestamp
    ? Date.parse(jackpotResponse.jackpotAmountTimestamp)
    : Date.now();

  return {
    status,
    amount: jackpotResponse.jackpotAmount ?? 0,
    amountPerSecond:
      jackpotResponse.isActive && !isPaused
        ? jackpotResponse.jackpotAmountChangePerSecond
        : 0,
    baseTimestamp,
  };
};

export const useProgressiveJackpot = () => {
  const { $jackpotService } = useServices();
  const jackpotState = ref<IProgressiveJackpotState | null>(null);
  const loading = ref(true);
  const error = ref<string | null>(null);

  let tickTimer: ReturnType<typeof setInterval> | null = null;
  let refreshTimer: ReturnType<typeof setInterval> | null = null;

  const tick = ref<number>(0);

  const isActive = computed(() => jackpotState.value?.status === 'active');

  const liveAmount = computed(() => {
    void tick.value;
    if (!jackpotState.value) return 0;
    const aps =
      isActive.value && jackpotState.value.amountPerSecond > 0
        ? jackpotState.value.amountPerSecond
        : 0;
    const baseTs =
      jackpotState.value.baseTimestamp && jackpotState.value.baseTimestamp > 0
        ? jackpotState.value.baseTimestamp
        : Date.now();
    const elapsedSeconds = Math.max(0, (Date.now() - baseTs) / 1000);
    const growth = aps * elapsedSeconds;
    const value = (jackpotState.value.amount ?? 0) + growth;
    return Math.max(0, Math.round(value * 100) / 100);
  });

  const startTicking = () => {
    if (tickTimer) clearInterval(tickTimer);
    tickTimer = setInterval(() => {
      if (isActive.value) tick.value++;
    }, 3000);
  };

  const startPeriodicRefresh = () => {
    if (refreshTimer) clearInterval(refreshTimer);
    refreshTimer = setInterval(async () => await fetchJackpot(), 60000);
  };

  const stop = () => {
    if (tickTimer) clearInterval(tickTimer);
    tickTimer = null;
    if (refreshTimer) clearInterval(refreshTimer);
    refreshTimer = null;
  };

  const fetchJackpot = async () => {
    try {
      const jackpotResponse = await $jackpotService.fetchProgressiveJackpots();
      jackpotState.value = jackpotResponse ? mapJackpot(jackpotResponse) : null;
      error.value = null;
    } catch (e) {
      error.value = e?.message || 'progressive-jackpots-general-error';
    } finally {
      loading.value = false;
    }
  };

  onMounted(async () => {
    await fetchJackpot();
    startTicking();
    startPeriodicRefresh();
  });
  onBeforeUnmount(() => stop());

  return {
    error,
    jackpotState,
    liveAmount,
    loading,
  };
};

export const useFixedJackpots = () => {
  const gameStore = useGameStore();

  const getActiveFixedJackpot = (vertical?: string): IFixedJackpotEvent => {
    const fixedJackpots = gameStore.returnFixedJackpots(vertical);
    if (!fixedJackpots?.length) return;
    const activeFixedJackpots = fixedJackpots.filter((jackpot) => {
      const today = new Date();
      const startDate = jackpot.activeFromDateTime;
      const endDate = jackpot.activeToDateTime;
      return today >= startDate && today <= endDate;
    });
    if (activeFixedJackpots.length > 0) {
      activeFixedJackpots.sort(
        (a, b) => +(a.activeToDateTime as Date) - +(b.activeToDateTime as Date),
      );
      return activeFixedJackpots[0];
    }
    //upcoming fixed jackpots
    return fixedJackpots?.reduce((min, current) => {
      const earliestEndDate = min.activeToDateTime;
      const currentEndDate = current.activeToDateTime;
      return currentEndDate < earliestEndDate ? current : min;
    });
  };

  const mapFixedJackpotCategories = (category: string) => {
    switch (category?.toLowerCase()) {
      case 'slots':
        return 'spingames';
      case 'crash games':
        return 'crashgames';
      default:
        return category?.toLowerCase().replaceAll(' ', '');
    }
  };

  return { getActiveFixedJackpot, mapFixedJackpotCategories };
};
